import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  IconButton,
  Tooltip,
  Button,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import {
  disableLearner,
  getAllLearners,
  getInactiveLearners,
} from "../../../services/adminServices/CoeServices";
import { getLearnersByCoeAndLab } from "../../../services/mentorServices/MentorServices";
import { useAttendance } from "../../../context/AttendanceContext";
import EditStudentModal from "./EditStudentModal";
import AddStudentModal from "./AddStudentModal";
import FileUploadModal from "./FileUploadModal";
import SearchIcon from "@mui/icons-material/Search";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
  outline: "none !important",
};

const StudentListModal = ({ open, onClose, studentData, fetchCoeData, isMentorView = false, status = 'active' }) => {
  const [rows, setRows] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addStudentModalOpen, setAddStudentModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openFileUpload, setFileUploadOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [learnerToDisable, setLearnerToDisable] = useState(null);
  const { isAttendanceMarked, markAttendanceAction, markingAttendanceId, recordAttendance } = useAttendance();

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user')) || {};
  } catch (e) {
    user = {};
  }
  const isMentor = isMentorView || user?.role === 'mentor';

  const targetCoeId = studentData?.ejet_coe_center_id || studentData?.base_coe?.id || studentData?.id;

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isMentorView) {
        const coeId = studentData?.ejet_coe_center_id || studentData?.base_coe?.id;
        const labId = studentData?.id;
        if (!coeId || !labId) {
          setRows([]);
          return;
        }
        const response = await getLearnersByCoeAndLab(coeId, labId);
        const learnersList = response?.data?.data?.learners || response?.data?.data || response?.data?.learners || (Array.isArray(response?.data) ? response?.data : []);
        const validLearners = Array.isArray(learnersList) ? learnersList : [];
        // Backend data is the source of truth for attendance status.
        // We do NOT sync to context here to avoid stale data across lab changes.
        setRows(validLearners);
      } else {
        const coeId = studentData?.ejet_coe_center_id || studentData?.base_coe?.id || studentData?.id;
        if (!coeId) {
          setRows([]);
          return;
        }
        const response = status === 'inactive' ? await getInactiveLearners(coeId) : await getAllLearners(coeId);
        setRows(response?.data?.data?.learners || response?.data?.data || response?.data?.learners || (Array.isArray(response?.data) ? response?.data : []));
      }
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearch("");
      setPage(0);
      setRows([]);
      fetchData();
    } else {
      setRows([]);
      setLoading(false);
    }
  }, [studentData, open]);

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setEditModalOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFileDownload = () => {
    try {
      const fileUrl = `${process.env.PUBLIC_URL}/SampleLearnerCSV.csv`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "SampleCSV.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("File downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download the file.");
    }
  };

  const handleConfirmDisable = (student) => {
    setLearnerToDisable(student);
    setConfirmOpen(true);
  };

  const handleConfirmYes = async () => {
    if (!learnerToDisable) return;
    try {
      const response = await disableLearner(learnerToDisable.learner_id || learnerToDisable.id);
      toast.success(response.data.message);
      fetchData();
      if (fetchCoeData) fetchCoeData();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || error?.error);
    } finally {
      setConfirmOpen(false);
      setLearnerToDisable(null);
    }
  };

  const handleConfirmNo = () => {
    setConfirmOpen(false);
    setLearnerToDisable(null);
  };

  const handleMarkAttendance = async (student) => {
    const learnerId = student?.learner_id || student?.id || student?.learnerId;
    const success = await markAttendanceAction(learnerId);
    if (success) {
      setRows(prevRows =>
        prevRows.map(r => {
          const rId = r?.learner_id || r?.id || r?.learnerId;
          if (String(rId) === String(learnerId)) {
            return { ...r, attendanceStatus: 'PRESENT' };
          }
          return r;
        })
      );
    }
  };

  const handleDataDownload = () => {
    if (rows.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const headers = [
      "name",
      "lab_name",
      "institution_email",
      "email",
      "mobile",
      "device_id",
      "roll_num",
      "current_semester",
      "enrolment_year",
      "passout_year",
      "stream",
    ];

    const csvRows = [
      headers.join(","),
      ...rows.map((student) =>
        [
          student.name || null,
          student.lab?.lab_name || student.labname || student.lab_name || student.labName || null,
          student.institution_email || null,
          student.email || null,
          student.mobile || null,
          student.device_id || null,
          student.roll_num || null,
          student.current_semester || null,
          student.enrolment_year || null,
          student.passout_year || null,
          student.stream || null,
        ].join(",")
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "LearnersData.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Data CSV downloaded successfully!");
  };

  const filteredLearners = rows.filter((learner) => {
    const searchTerm = search.toLowerCase();
    return (
      learner.name?.toLowerCase().includes(searchTerm) ||
      learner.email?.toLowerCase().includes(searchTerm) ||
      learner.groupName?.toLowerCase().includes(searchTerm) ||
      learner.lab?.lab_name?.toLowerCase().includes(searchTerm) ||
      learner.labname?.toLowerCase().includes(searchTerm) ||
      learner.lab_name?.toLowerCase().includes(searchTerm) ||
      learner.institution_email?.toLowerCase().includes(searchTerm) ||
      learner.institutionEmail?.toLowerCase().includes(searchTerm) ||
      learner.mobile?.toString().toLowerCase().includes(searchTerm) ||
      learner.roll_num?.toString().toLowerCase().includes(searchTerm) ||
      learner.current_semester?.toString().toLowerCase().includes(searchTerm) ||
      learner.enrolment_year?.toString().toLowerCase().includes(searchTerm) ||
      learner.passout_year?.toString().toLowerCase().includes(searchTerm) ||
      learner.stream?.toLowerCase().includes(searchTerm)
    );
  });

  const paginatedRows = filteredLearners.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const highlightText = (text, search) => {
    if (!text) return text;
    if (!search) return text;
    const parts = text.toString().split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "yellow" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h6" sx={{ mb: 0, whiteSpace: "nowrap" }}>
                {status === 'inactive' ? 'Inactive Learner Details' : 'Active Learner Details'}
              </Typography>
              <TextField
                sx={{ width: "250px" }}
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "nowrap" }}>
              {!isMentorView && status !== 'inactive' && (
                <Button
                  onClick={handleFileDownload}
                  variant="contained"
                  component="label"
                  color="primary"
                  sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
                >
                  Download Sample CSV
                </Button>
              )}
              <Button
                onClick={handleDataDownload}
                variant="contained"
                color="primary"
                sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
              >
                Download Learner Data
              </Button>
              {!isMentorView && status !== 'inactive' && (
                <>
                  <Button
                    onClick={() => setAddStudentModalOpen(true)}
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
                  >
                    Add Learner
                  </Button>
                  <Button
                    variant="contained"
                    component="label"
                    color="primary"
                    sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
                    onClick={() => setFileUploadOpen(true)}
                  >
                    Upload CSV
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "400px !important",
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                tableLayout: "auto",
                width: "max-content",
                minWidth: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Group Name</StyledTableCell>
                  <StyledTableCell>Lab Name</StyledTableCell>
                  <StyledTableCell>Institution Email</StyledTableCell>
                  <StyledTableCell>Personal Gmail</StyledTableCell>
                  <StyledTableCell>Mobile</StyledTableCell>
                  <StyledTableCell>Roll Number</StyledTableCell>
                  <StyledTableCell>Semester</StyledTableCell>
                  <StyledTableCell>Enrolment Year</StyledTableCell>
                  <StyledTableCell>Passout Year</StyledTableCell>
                  <StyledTableCell>Stream</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={12} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                          Loading learner details...
                        </Typography>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((student, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{highlightText(student?.name, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student?.groupName, search) || "-"}</StyledTableCell>
                      <StyledTableCell style={{ textTransform: "none" }}>{highlightText(student?.lab?.lab_name || student?.labname || student?.lab_name || student?.labName, search) || "-"}</StyledTableCell>
                      <StyledTableCell style={{ textTransform: "none" }}>{highlightText(student?.institution_email, search) || "-"}</StyledTableCell>
                      <StyledTableCell style={{ textTransform: "none" }}>{highlightText(student?.email, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student.mobile, search) || "-"}</StyledTableCell>
                      <StyledTableCell style={{ textTransform: "none" }}>{highlightText(student.roll_num, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student.current_semester, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student.enrolment_year, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student.passout_year, search) || "-"}</StyledTableCell>
                      <StyledTableCell>{highlightText(student.stream, search) || "-"}</StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => handleEdit(student)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isMentor && status !== 'inactive' && (() => {
                            const learnerId = student?.learner_id || student?.id || student?.learnerId;
                            const statusVal = String(student?.attendanceStatus || student?.attendance_status || '').toUpperCase();
                            // Use backend status as source of truth; fall back to context only when backend provides no status
                            const hasBackendStatus = statusVal !== '';
                            const isMarked = hasBackendStatus ? statusVal === 'PRESENT' : isAttendanceMarked(learnerId);
                            return isMarked ? (
                              <Button
                                variant="contained"
                                size="small"
                                disabled
                                startIcon={<CheckCircleIcon sx={{ fontSize: '15px !important' }} />}
                                sx={{
                                  textTransform: "capitalize",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "4px",
                                  minWidth: "auto",
                                  "&.Mui-disabled": {
                                    backgroundColor: "#2e7d32",
                                    color: "#ffffff",
                                    opacity: 0.9,
                                  },
                                }}
                              >
                                Attendance Marked
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                disabled={markingAttendanceId === String(learnerId)}
                                onClick={() => handleMarkAttendance(student)}
                                sx={{
                                  textTransform: "capitalize",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "4px",
                                  minWidth: "auto",
                                }}
                              >
                                {markingAttendanceId === String(learnerId) ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <CircularProgress size={14} color="inherit" />
                                    <span>Marking...</span>
                                  </Box>
                                ) : (
                                  "Mark Attendance"
                                )}
                              </Button>
                            );
                          })()}
                          {!isMentorView && (
                            <Tooltip title={status === 'inactive' ? "Enable" : "Disable"} arrow>
                              <IconButton size="small" aria-label={status === 'inactive' ? "enable" : "disable"} onClick={() => handleConfirmDisable(student)}>
                                {status === 'inactive' ? <CheckCircleIcon fontSize="small" color="success" /> : <BlockIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      {search ? "No student found matching your search" : "No student data available"}
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 60, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Modal>

      <FileUploadModal
        open={openFileUpload}
        fetchData={fetchData}
        coeId={targetCoeId}
        handleClose={() => setFileUploadOpen(false)}
        fetchCoeData={fetchCoeData}
      />

      {addStudentModalOpen && (
        <AddStudentModal
          open={addStudentModalOpen}
          coeId={targetCoeId}
          onClose={() => setAddStudentModalOpen(false)}
          fetchData={fetchData}
          fetchCoeData={fetchCoeData}
        />
      )}

      {editModalOpen && (
        <EditStudentModal
          open={editModalOpen}
          student={selectedStudent}
          onClose={() => setEditModalOpen(false)}
          fetchData={fetchData}
          coeId={targetCoeId}
          isMentorView={isMentorView}
        />
      )}

      <Dialog
        open={confirmOpen}
        onClose={handleConfirmNo}
        PaperProps={{
          sx: {
            p: 3,
            borderRadius: 3,
            boxShadow: 6,
            minWidth: 400,
            maxWidth: 450,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            fontSize: '1.25rem',
            color: status === 'inactive' ? '#2e7d32' : '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {status === 'inactive' ? <CheckCircleIcon fontSize="medium" color="success" /> : <BlockIcon fontSize="medium" color="error" />}
          Confirm {status === 'inactive' ? "Enable" : "Disable"}
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ fontSize: 15, color: "#555", mt: 1 }}>
            Are you sure you want to {status === 'inactive' ? "enable" : "disable"} <strong>{learnerToDisable?.name}</strong>?
            <br />
            {status === 'inactive' ? "This action will restore their access." : "This action can restrict their access."}
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", mt: 2 }}>
          <Button
            onClick={handleConfirmNo}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "capitalize", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmYes}
            variant="contained"
            color={status === 'inactive' ? "success" : "error"}
            sx={{ textTransform: "capitalize", borderRadius: 2 }}
          >
            Yes, {status === 'inactive' ? "Enable" : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentListModal;
